import {FC, useEffect, useState, useCallback, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {Box, Typography, Button, CircularProgress, TextField} from '@mui/material';
import {useIntl} from 'react-intl';
import {loadStripe, Stripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
    useGetGuestConnectorQuery,
    useCreateGuestSessionMutation,
    useLazyGetGuestSessionQuery,
    useStartGuestChargingMutation,
    useStopGuestChargingMutation,
    useSendGuestReceiptMutation,
} from 'src/api/guest';
import {
    GuestPageState,
    GuestSessionStatus,
    GuestSessionDTO,
    GuestSessionFullDTO,
} from 'src/types/guest';
import {ConnectorStatusEnum} from 'src/enums/chargers';
import {LanguageEnum, LanguageShortEnum} from 'src/enums/common';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {changeLang} from 'src/stores/common/commonSlice';
import {LangSelect} from 'src/components/common/LangSelect';
import {LogoBlack} from 'src/components/icons/LogoBlack';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {GuestPaymentForm} from 'src/components/guest/GuestPaymentForm';
import {useGuestSessionWebSocket} from 'src/hooks/useGuestSessionWebSocket';
import {guestChargingSx} from './GuestCharging.sx';

// Cache for Stripe promises by publishable key
const stripePromiseCache = new Map<string, Promise<Stripe | null>>();

const getStripePromise = (publishableKey: string): Promise<Stripe | null> => {
    if (!stripePromiseCache.has(publishableKey)) {
        stripePromiseCache.set(publishableKey, loadStripe(publishableKey));
    }
    return stripePromiseCache.get(publishableKey)!;
};

// Remembers that this browser has already been shown the guest default, so a guest who switches
// language keeps their choice on the next visit. Deliberately in localStorage, not sessionStorage:
// the point is to survive the tab that scanned the code.
const GUEST_LANG_DEFAULTED = 'guestLangDefaulted';

// Session storage keys are scoped per connector UUID to support multiple connectors
const getSessionKey = (connectorUuid: string) => `guestSession_${connectorUuid}`;
const getStripeSecretKey = (connectorUuid: string) => `guestStripeCS_${connectorUuid}`;
const getStripePubKey = (connectorUuid: string) => `guestStripePK_${connectorUuid}`;

// Helper functions for formatting
const formatPrice = (value: number, currency: string): string => {
    const amount = value / 10000;
    return `${amount.toFixed(2)} ${currency}`;
};

const formatEnergy = (watts: number): string => {
    const kWh = watts / 1000;
    return kWh.toFixed(2);
};

const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
        return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
};

const calculateCurrentCost = (wattsConsumed: number, pricePerKwh: number): number => {
    return (wattsConsumed * pricePerKwh) / 1000;
};

export const GuestCharging: FC = () => {
    const intl = useIntl();
    const {connectorUuid} = useParams<{connectorUuid: string}>();

    // Page state
    const [pageState, setPageState] = useState<GuestPageState>('loading');
    const [sessionData, setSessionData] = useState<GuestSessionDTO | null>(null);
    const [fullSessionData, setFullSessionData] = useState<GuestSessionFullDTO | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [receiptEmail, setReceiptEmail] = useState<string>('');
    const [receiptError, setReceiptError] = useState<string>('');

    // Ref to always have the current session UUID (avoids stale closures in useEffect)
    const sessionUuidRef = useRef<string | undefined>();
    // Guard against duplicate startCharging calls
    const isStartingRef = useRef(false);

    // API queries and mutations
    const {
        data: connectorData,
        error: connectorError,
    } = useGetGuestConnectorQuery(connectorUuid || '', {
        skip: !connectorUuid,
    });

    const [createSession, {isLoading: isCreatingSession}] = useCreateGuestSessionMutation();
    const [fetchSession] = useLazyGetGuestSessionQuery();
    const [startCharging] = useStartGuestChargingMutation();
    const [stopCharging, {isLoading: isStoppingCharging}] = useStopGuestChargingMutation();
    const [sendReceipt, {isLoading: isSendingReceipt}] = useSendGuestReceiptMutation();

    // The receipt is written in the language the guest was reading the page in. The interface speaks
    // four and the mail templates are written in three, so Ukrainian is served the English receipt -
    // the same decision, for the same reason, as the RFID terms in `RequestRfid`.
    const {lang, authInfo} = useAppSelector((state) => state.common);
    const dispatch = useAppDispatch();

    /*
     * Guest charging opens in French.
     *
     * Every station that has a guest tariff stands in French-speaking Switzerland or in France, and
     * whoever scans the code is a stranger whose language nobody knows - so the page opens in the one
     * its readers are most likely to have, rather than in the store's English, which is the default
     * for a dashboard nobody reaches by scanning anything.
     *
     * Two guards, because `common.lang` is shared with the dashboard and persisted. It is set only for
     * a visitor who is not signed in - an employee who scans the same code keeps their own interface -
     * and only once per browser, so a guest who picks another language is never overridden on a later
     * visit.
     */
    const isSignedIn = Object.keys(authInfo).length > 0;
    useEffect(() => {
        if (isSignedIn || localStorage.getItem(GUEST_LANG_DEFAULTED)) {
            return;
        }
        try {
            localStorage.setItem(GUEST_LANG_DEFAULTED, '1');
        } catch {
            // A browser that refuses storage gets the default on every visit; that is the lesser harm.
        }
        dispatch(changeLang(LanguageShortEnum.FR));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const receiptLanguage: Record<LanguageShortEnum, LanguageEnum> = {
        [LanguageShortEnum.EN]: LanguageEnum.ENGLISH,
        [LanguageShortEnum.FR]: LanguageEnum.FRENCH,
        [LanguageShortEnum.DE]: LanguageEnum.DEUTSCH,
        [LanguageShortEnum.UK]: LanguageEnum.ENGLISH,
    };

    // WebSocket hook for real-time updates (connector status and session updates)
    const {wsData, connectorStatus: wsConnectorStatus, isConnected} = useGuestSessionWebSocket({
        connectorUuid: connectorUuid || null,
        sessionUuid: fullSessionData?.uuid || sessionData?.uuid || null,
    });

    // Keep the session UUID ref in sync for use in useEffect handlers
    sessionUuidRef.current = fullSessionData?.uuid || sessionData?.uuid;

    // Use WebSocket connector status if available, otherwise fall back to API data
    const currentConnectorStatus = wsConnectorStatus || connectorData?.status;

    // Initialize Stripe when we have session data or restored from storage
    useEffect(() => {
        if (sessionData?.publishableKey && connectorUuid) {
            setStripePromise(getStripePromise(sessionData.publishableKey));
            // Save Stripe data to sessionStorage for page reload recovery
            sessionStorage.setItem(getStripeSecretKey(connectorUuid), sessionData.clientSecret);
            sessionStorage.setItem(getStripePubKey(connectorUuid), sessionData.publishableKey);
        }
    }, [sessionData?.publishableKey, sessionData?.clientSecret, connectorUuid]);

    // Check for existing session on mount
    useEffect(() => {
        if (!connectorUuid) return;

        const savedSessionUuid = sessionStorage.getItem(getSessionKey(connectorUuid));
        if (savedSessionUuid) {
            // Try to restore the session for this connector
            fetchSession(savedSessionUuid)
                .unwrap()
                .then((session) => {
                    setFullSessionData(session);

                    // If the session is PENDING, try to restore Stripe data
                    if (session.status === GuestSessionStatus.PENDING) {
                        const savedClientSecret = sessionStorage.getItem(getStripeSecretKey(connectorUuid));
                        const savedPublishableKey = sessionStorage.getItem(getStripePubKey(connectorUuid));

                        if (savedClientSecret && savedPublishableKey) {
                            // Restore sessionData with Stripe credentials
                            setSessionData({
                                uuid: session.uuid,
                                status: session.status,
                                clientSecret: savedClientSecret,
                                publishableKey: savedPublishableKey,
                                pricePerKwh: session.pricePerKwh,
                                currency: session.currency,
                                holdAmount: session.holdAmount,
                            });
                            setStripePromise(getStripePromise(savedPublishableKey));
                        }
                    } else {
                        // Session is no longer PENDING, clear Stripe data from storage
                        sessionStorage.removeItem(getStripeSecretKey(connectorUuid));
                        sessionStorage.removeItem(getStripePubKey(connectorUuid));
                    }

                    updatePageStateFromSession(session);
                })
                .catch((error: any) => {
                    // Only clear storage if session not found (404)
                    // For other errors (network, 500), keep the session UUID for retry
                    if (error?.status === 404) {
                        sessionStorage.removeItem(getSessionKey(connectorUuid));
                        sessionStorage.removeItem(getStripeSecretKey(connectorUuid));
                        sessionStorage.removeItem(getStripePubKey(connectorUuid));
                    }
                    setPageState('connector_info');
                });
        }
    }, [connectorUuid]);

    // Update page state based on connector data
    useEffect(() => {
        if (connectorError) {
            setPageState('not_found');
            return;
        }
        if (connectorData && pageState === 'loading') {
            const savedSessionUuid = connectorUuid ? sessionStorage.getItem(getSessionKey(connectorUuid)) : null;
            if (!savedSessionUuid) {
                setPageState('connector_info');
            }
        }
    }, [connectorData, connectorError, pageState, connectorUuid]);

    // Refresh session data when WebSocket reconnects
    const wasConnectedRef = useRef(false);
    useEffect(() => {
        if (isConnected && !wasConnectedRef.current) {
            const uuid = sessionUuidRef.current;
            if (uuid) {
                fetchSession(uuid)
                    .unwrap()
                    .then((session) => {
                        setFullSessionData(session);
                        updatePageStateFromSession(session);
                    })
                    .catch(() => {});
            }
        }
        wasConnectedRef.current = isConnected;
    }, [isConnected]);

    // Handle WebSocket updates
    useEffect(() => {
        if (!wsData) return;

        if (wsData.status === GuestSessionStatus.COMPLETED) {
            // Completed - fetch full session data from the backend
            const uuid = sessionUuidRef.current;
            if (uuid) {
                fetchSession(uuid)
                    .unwrap()
                    .then((session) => {
                        setFullSessionData(session);
                        setPageState('completed');
                    })
                    .catch(() => {
                        // Fallback to WS data if fetch fails
                        setFullSessionData((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      status: GuestSessionStatus.COMPLETED,
                                      finalAmount: 'finalAmount' in wsData ? wsData.finalAmount : prev.finalAmount,
                                      wattsConsumed: 'wattsConsumed' in wsData ? wsData.wattsConsumed : prev.wattsConsumed,
                                  }
                                : null
                        );
                        setPageState('completed');
                    });
            }
        } else if (wsData.status === GuestSessionStatus.CHARGING) {
            // Charging started or progress update
            if ('wattsConsumed' in wsData) {
                setFullSessionData((prev) =>
                    prev
                        ? {
                              ...prev,
                              wattsConsumed: wsData.wattsConsumed,
                              chargingTimeMinutes: wsData.chargingTimeMinutes,
                          }
                        : null
                );
            }
            setPageState('charging');
        } else if (wsData.status === GuestSessionStatus.STARTING) {
            // Station is starting the charge
            setPageState('starting_charge');
        } else if (wsData.status === GuestSessionStatus.STOPPING) {
            // Station is stopping the charge
            setPageState('stopping_charge');
        } else if (wsData.status === GuestSessionStatus.AUTHORIZED) {
            // Payment authorized - automatically start charging
            const uuid = sessionUuidRef.current;
            if (uuid && !isStartingRef.current) {
                setPageState('starting_charge');
                isStartingRef.current = true;
                startCharging(uuid)
                    .unwrap()
                    .then((session) => {
                        setFullSessionData(session);
                        // Stay in starting_charge, WebSocket will notify when CHARGING
                        if (session.status === GuestSessionStatus.CHARGING) {
                            setPageState('charging');
                        }
                    })
                    .catch((error: any) => {
                        setErrorMessage(error?.data?.message || 'Failed to start charging');
                        setPageState('error');
                    })
                    .finally(() => {
                        isStartingRef.current = false;
                    });
            }
        } else if (wsData.status === GuestSessionStatus.FAILED) {
            setErrorMessage(wsData.message || intl.formatMessage({id: 'guestCharging.paymentFailed'}));
            setPageState('error');
        } else if (wsData.status === GuestSessionStatus.CANCELLED) {
            setPageState('cancelled');
        }
    }, [wsData]);

    // Handle Stripe redirect (3DS)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');

        if (paymentIntentClientSecret && stripePromise && connectorUuid) {
            stripePromise.then((stripe) => {
                if (stripe) {
                    stripe.retrievePaymentIntent(paymentIntentClientSecret).then(({paymentIntent}) => {
                        if (paymentIntent?.status === 'requires_capture') {
                            // Payment authorized, session should be AUTHORIZED
                            const savedSessionUuid = sessionStorage.getItem(getSessionKey(connectorUuid));
                            if (savedSessionUuid) {
                                fetchSession(savedSessionUuid)
                                    .unwrap()
                                    .then((session) => {
                                        setFullSessionData(session);
                                        updatePageStateFromSession(session);
                                    });
                            }
                        }
                    });
                }
            });
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [stripePromise]);

    const updatePageStateFromSession = (session: GuestSessionFullDTO) => {
        switch (session.status) {
            case GuestSessionStatus.PENDING:
                setPageState('payment');
                break;
            case GuestSessionStatus.AUTHORIZED:
                // Auto-start charging when the session is authorized (e.g., after page reload)
                if (isStartingRef.current) {
                    setPageState('starting_charge');
                    break;
                }
                setPageState('starting_charge');
                isStartingRef.current = true;
                startCharging(session.uuid)
                    .unwrap()
                    .then((updatedSession) => {
                        setFullSessionData(updatedSession);
                        // Stay in starting_charge, WebSocket will notify when CHARGING
                        if (updatedSession.status === GuestSessionStatus.CHARGING) {
                            setPageState('charging');
                        }
                    })
                    .catch((error: any) => {
                        setErrorMessage(error?.data?.message || 'Failed to start charging');
                        setPageState('error');
                    })
                    .finally(() => {
                        isStartingRef.current = false;
                    });
                break;
            case GuestSessionStatus.STARTING:
                setPageState('starting_charge');
                break;
            case GuestSessionStatus.CHARGING:
                setPageState('charging');
                break;
            case GuestSessionStatus.STOPPING:
                setPageState('stopping_charge');
                break;
            case GuestSessionStatus.COMPLETED:
                setPageState('completed');
                break;
            case GuestSessionStatus.FAILED:
                setPageState('error');
                break;
            case GuestSessionStatus.CANCELLED:
                setPageState('cancelled');
                break;
            default:
                setPageState('connector_info');
        }
    };

    const handleProceedToPayment = useCallback(async () => {
        if (!connectorUuid) return;

        try {
            const result = await createSession({
                connectorUuid,
            }).unwrap();

            setSessionData(result);
            sessionStorage.setItem(getSessionKey(connectorUuid), result.uuid);
            setPageState('payment');
        } catch {
            setErrorMessage('');
            setPageState('error');
        }
    }, [connectorUuid, createSession]);

    const handlePaymentSuccess = useCallback(() => {
        setPageState('processing');
    }, []);

    const handlePaymentError = useCallback((message: string) => {
        setErrorMessage(message);
        setPageState('error');
    }, []);

    const handleStopCharging = useCallback(async () => {
        const uuid = fullSessionData?.uuid || sessionData?.uuid;
        if (!uuid) return;

        try {
            setPageState('stopping_charge');
            const session = await stopCharging(uuid).unwrap();
            setFullSessionData(session);
            // Check if the session is already completed or still stopping
            if (session.status === GuestSessionStatus.COMPLETED) {
                setPageState('completed');
            }
            // If status is STOPPING, WebSocket will notify when completed
        } catch (error: any) {
            setErrorMessage(error?.data?.message || 'Failed to stop charging');
            setPageState('error');
        }
    }, [fullSessionData?.uuid, sessionData?.uuid, stopCharging]);

    const handleSendReceipt = useCallback(async () => {
        const email = receiptEmail.trim();
        // Checked here only to spare the round trip; the address is validated again on the server.
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setReceiptError(intl.formatMessage({id: 'guestCharging.invalidEmail'}));
            return;
        }

        const uuid = sessionUuidRef.current;
        if (!uuid) return;

        setReceiptError('');
        try {
            const session = await sendReceipt({
                uuid,
                email,
                language: receiptLanguage[(lang as LanguageShortEnum) ?? LanguageShortEnum.EN],
            }).unwrap();
            setFullSessionData(session);
        } catch (error: any) {
            setReceiptError(
                error?.data?.message || intl.formatMessage({id: 'guestCharging.receiptFailed'})
            );
        }
    }, [receiptEmail, sendReceipt, intl, lang]);

    const handleTryAgain = useCallback(() => {
        if (connectorUuid) {
            sessionStorage.removeItem(getSessionKey(connectorUuid));
            sessionStorage.removeItem(getStripeSecretKey(connectorUuid));
            sessionStorage.removeItem(getStripePubKey(connectorUuid));
        }
        setSessionData(null);
        setFullSessionData(null);
        setStripePromise(null);
        setErrorMessage('');
        setPageState('connector_info');
    }, [connectorUuid]);

    // Get the current price per kWh and currency
    const pricePerKwh = fullSessionData?.pricePerKwh || sessionData?.pricePerKwh || connectorData?.pricePerKwh || 0;
    const currency = fullSessionData?.currency || sessionData?.currency || connectorData?.currency || 'CHF';
    const holdAmount = fullSessionData?.holdAmount || sessionData?.holdAmount || connectorData?.holdAmount || 0;

    // Render based on page state
    const renderContent = () => {
        switch (pageState) {
            case 'loading':
                return (
                    <Box sx={guestChargingSx.loadingContainer}>
                        <CircularProgress size={48} />
                        <Typography sx={guestChargingSx.loadingText}>
                            {intl.formatMessage({id: 'loading'})}
                        </Typography>
                    </Box>
                );

            case 'not_found':
                return (
                    <Box sx={guestChargingSx.errorCard}>
                        <ErrorIcon sx={guestChargingSx.errorIcon} />
                        <Typography sx={guestChargingSx.errorTitle}>
                            {intl.formatMessage({id: 'guestCharging.connectorNotFound'})}
                        </Typography>
                        <Typography sx={guestChargingSx.errorMessage}>
                            {intl.formatMessage({id: 'guestCharging.checkQrCode'})}
                        </Typography>
                    </Box>
                );

            case 'connector_info':
                return (
                    <>
                        {/* Location info card */}
                        <Box sx={guestChargingSx.card}>
                            <Typography sx={guestChargingSx.locationName}>
                                {connectorData?.locationName}
                            </Typography>
                            <Typography sx={guestChargingSx.locationAddress}>
                                {connectorData?.locationAddress}
                            </Typography>
                        </Box>

                        {/* Charger info card */}
                        <Box sx={guestChargingSx.card}>
                            <Box sx={guestChargingSx.priceRow}>
                                <Typography sx={guestChargingSx.priceLabel}>
                                    {intl.formatMessage({id: 'guestCharging.chargePoint'})}
                                </Typography>
                                <Typography sx={guestChargingSx.priceValue}>
                                    {connectorData?.chargePointId}
                                </Typography>
                            </Box>
                            <Box sx={guestChargingSx.priceRow}>
                                <Typography sx={guestChargingSx.priceLabel}>
                                    {intl.formatMessage({id: 'guestCharging.model'})}
                                </Typography>
                                <Typography sx={guestChargingSx.priceValue}>
                                    {connectorData?.chargePointModel}
                                </Typography>
                            </Box>
                            {currentConnectorStatus && (
                                <Box sx={guestChargingSx.priceRow}>
                                    <Typography sx={guestChargingSx.priceLabel}>
                                        {intl.formatMessage({id: 'status'})}
                                    </Typography>
                                    <ColoredStatus label={currentConnectorStatus} fontSize={12} />
                                </Box>
                            )}
                        </Box>

                        {/* Pricing card */}
                        <Box sx={guestChargingSx.card}>
                            <Box sx={guestChargingSx.priceRow}>
                                <Typography sx={guestChargingSx.priceLabel}>
                                    {intl.formatMessage({id: 'guestCharging.pricePerKwh'})}
                                </Typography>
                                <Typography sx={guestChargingSx.priceValue}>
                                    {formatPrice(pricePerKwh, currency)}/kWh
                                </Typography>
                            </Box>
                            <Box sx={guestChargingSx.priceRow}>
                                <Typography sx={guestChargingSx.priceLabel}>
                                    {intl.formatMessage({id: 'guestCharging.holdAmount'})}
                                </Typography>
                                <Typography sx={guestChargingSx.priceValue}>
                                    {formatPrice(holdAmount, currency)}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                );

            case 'payment':
                return (
                    <>
                        {/* Stripe Payment Element */}
                        {stripePromise && sessionData?.clientSecret && (
                            <Box sx={{display: 'flex', flexDirection: 'column', flex: 1}}>
                                <Box sx={guestChargingSx.card}>
                                    <Elements
                                        stripe={stripePromise}
                                        options={{
                                            clientSecret: sessionData.clientSecret,
                                            appearance: {
                                                theme: 'stripe',
                                                variables: {
                                                    fontFamily: 'inherit',
                                                    borderRadius: '8px',
                                                    colorPrimary: '#1976d2',
                                                    spacingUnit: '4px',
                                                    fontSizeBase: '16px',
                                                },
                                                rules: {
                                                    '.Input': {
                                                        padding: '12px 14px',
                                                    },
                                                    '.Label': {
                                                        marginBottom: '6px',
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <GuestPaymentForm
                                            onSuccess={handlePaymentSuccess}
                                            onError={handlePaymentError}
                                        />
                                    </Elements>
                                </Box>
                            </Box>
                        )}
                    </>
                );

            case 'processing':
                return (
                    <Box sx={guestChargingSx.loadingContainer}>
                        <CircularProgress size={48} />
                        <Typography sx={guestChargingSx.loadingText}>
                            {intl.formatMessage({id: 'guestCharging.processingPayment'})}
                        </Typography>
                    </Box>
                );

            case 'starting_charge':
                return (
                    <>
                        <Box sx={guestChargingSx.card}>
                            <Typography sx={guestChargingSx.locationName}>
                                {connectorData?.locationName || fullSessionData?.connector?.locationName}
                            </Typography>
                            <Typography sx={guestChargingSx.locationAddress}>
                                {connectorData?.locationAddress || fullSessionData?.connector?.locationAddress}
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.loadingContainer}>
                            <CircularProgress size={48} />
                            <Typography sx={guestChargingSx.loadingText}>
                                {intl.formatMessage({id: 'guestCharging.startingCharging'})}
                            </Typography>
                        </Box>
                    </>
                );

            case 'charging':
                const wattsConsumed = fullSessionData?.wattsConsumed || 0;
                const chargingTime = fullSessionData?.chargingTimeMinutes || 0;
                const currentCost = calculateCurrentCost(wattsConsumed, pricePerKwh);

                return (
                    <>
                        <Box sx={guestChargingSx.progressCard}>
                            <Typography sx={guestChargingSx.progressValue}>
                                {formatEnergy(wattsConsumed)}
                            </Typography>
                            <Typography sx={guestChargingSx.progressUnit}>kWh</Typography>

                            <Box sx={guestChargingSx.progressStats}>
                                <Box sx={guestChargingSx.statItem}>
                                    <Typography sx={guestChargingSx.statValue}>
                                        {formatDuration(chargingTime)}
                                    </Typography>
                                    <Typography sx={guestChargingSx.statLabel}>
                                        {intl.formatMessage({id: 'guestCharging.chargingTime'})}
                                    </Typography>
                                </Box>
                                <Box sx={guestChargingSx.statItem}>
                                    <Typography sx={guestChargingSx.statValue}>
                                        {formatPrice(currentCost, currency)}
                                    </Typography>
                                    <Typography sx={guestChargingSx.statLabel}>
                                        {intl.formatMessage({id: 'guestCharging.currentCost'})}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={guestChargingSx.card}>
                            <Typography sx={guestChargingSx.locationName}>
                                {connectorData?.locationName || fullSessionData?.connector?.locationName}
                            </Typography>
                            <Typography sx={guestChargingSx.locationAddress}>
                                {connectorData?.locationAddress || fullSessionData?.connector?.locationAddress}
                            </Typography>
                            <Box sx={{...guestChargingSx.priceRow, marginTop: 1}}>
                                <Typography sx={guestChargingSx.priceLabel}>
                                    {intl.formatMessage({id: 'guestCharging.chargePoint'})}
                                </Typography>
                                <Typography sx={guestChargingSx.priceValue}>
                                    {connectorData?.chargePointId || fullSessionData?.connector?.chargePointId}
                                </Typography>
                            </Box>
                            {currentConnectorStatus && (
                                <Box sx={guestChargingSx.priceRow}>
                                    <Typography sx={guestChargingSx.priceLabel}>
                                        {intl.formatMessage({id: 'status'})}
                                    </Typography>
                                    <ColoredStatus label={currentConnectorStatus} fontSize={12} />
                                </Box>
                            )}
                        </Box>
                    </>
                );

            case 'stopping_charge':
                return (
                    <Box sx={guestChargingSx.receiptCard}>
                        <Box sx={guestChargingSx.receiptHeader}>
                            <CircularProgress size={36} sx={{marginBottom: '4px', color: '#666'}} />
                            <Typography sx={guestChargingSx.receiptTitle}>
                                {intl.formatMessage({id: 'guestCharging.stoppingCharging'})}
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.location'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>
                                {connectorData?.locationName || fullSessionData?.connector?.locationName}
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.energyConsumed'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>...</Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.chargingTime'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>...</Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptTotal}>
                            <Typography sx={guestChargingSx.receiptTotalLabel}>
                                {intl.formatMessage({id: 'guestCharging.totalCharged'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptTotalValue}>...</Typography>
                        </Box>
                    </Box>
                );

            case 'completed':
                const finalWatts = fullSessionData?.wattsConsumed || 0;
                const finalTime = fullSessionData?.chargingTimeMinutes || 0;
                const finalAmount = fullSessionData?.finalAmount || 0;

                return (
                    <Box sx={guestChargingSx.receiptCard}>
                        <Box sx={guestChargingSx.receiptHeader}>
                            <CheckCircleIcon sx={guestChargingSx.receiptIcon} />
                            <Typography sx={guestChargingSx.receiptTitle}>
                                {intl.formatMessage({id: 'guestCharging.thankYou'})}
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.location'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>
                                {connectorData?.locationName || fullSessionData?.connector?.locationName}
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.energyConsumed'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>
                                {formatEnergy(finalWatts)} kWh
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.chargingTime'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>
                                {formatDuration(finalTime)}
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptRow}>
                            <Typography sx={guestChargingSx.receiptLabel}>
                                {intl.formatMessage({id: 'guestCharging.pricePerKwh'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptValue}>
                                {formatPrice(pricePerKwh, currency)}/kWh
                            </Typography>
                        </Box>

                        <Box sx={guestChargingSx.receiptTotal}>
                            <Typography sx={guestChargingSx.receiptTotalLabel}>
                                {intl.formatMessage({id: 'guestCharging.totalCharged'})}
                            </Typography>
                            <Typography sx={guestChargingSx.receiptTotalValue}>
                                {formatPrice(finalAmount, currency)}
                            </Typography>
                        </Box>

                        {/*
                          * The screen is the only copy of this receipt, and it is gone with the tab.
                          * One address, asked for after the money was taken and never before it: the
                          * server sends a single receipt per session, so once it has gone there is
                          * nothing left to offer and the form is replaced by what happened.
                          */}
                        <Box sx={guestChargingSx.receiptEmailBlock}>
                            {fullSessionData?.receiptSentAt ? (
                                <Typography sx={guestChargingSx.receiptEmailSent}>
                                    <CheckCircleIcon sx={{fontSize: 18}} />
                                    {intl.formatMessage({id: 'guestCharging.receiptSent'})}
                                </Typography>
                            ) : (
                                <>
                                    <Typography sx={guestChargingSx.receiptEmailLabel}>
                                        {intl.formatMessage({id: 'guestCharging.emailOptional'})}
                                    </Typography>
                                    <Typography sx={guestChargingSx.receiptEmailHint}>
                                        {intl.formatMessage({id: 'guestCharging.emailDescription'})}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="email"
                                        autoComplete="email"
                                        inputMode="email"
                                        value={receiptEmail}
                                        disabled={isSendingReceipt}
                                        error={!!receiptError}
                                        onChange={(event) => {
                                            setReceiptEmail(event.target.value);
                                            setReceiptError('');
                                        }}
                                        placeholder={intl.formatMessage({
                                            id: 'guestCharging.emailPlaceholder',
                                        })}
                                        sx={guestChargingSx.receiptEmailInput}
                                    />
                                    {receiptError && (
                                        <Typography sx={guestChargingSx.receiptEmailError}>
                                            {receiptError}
                                        </Typography>
                                    )}
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        disabled={!receiptEmail.trim() || isSendingReceipt}
                                        onClick={handleSendReceipt}
                                        sx={guestChargingSx.receiptEmailButton}
                                    >
                                        {isSendingReceipt
                                            ? intl.formatMessage({id: 'guestCharging.sendingReceipt'})
                                            : intl.formatMessage({id: 'guestCharging.sendReceipt'})}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                );

            case 'error':
            case 'cancelled':
                return (
                    <Box sx={guestChargingSx.errorCard}>
                        <ErrorIcon sx={guestChargingSx.errorIcon} />
                        <Typography sx={guestChargingSx.errorTitle}>
                            {pageState === 'cancelled'
                                ? intl.formatMessage({id: 'guestCharging.sessionExpired'})
                                : intl.formatMessage({id: 'guestCharging.errorOccurred'})}
                        </Typography>
                        <Typography sx={guestChargingSx.errorMessage}>
                            {errorMessage || intl.formatMessage({id: 'guestCharging.contactSupport'})}
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    // Render footer button based on state
    const renderFooter = () => {
        switch (pageState) {
            case 'connector_info':
                // Only PREPARING status allows starting a session
                // User must plug in the cable first, then scan QR code
                // Uses real-time connector status from WebSocket when available
                const isPreparing = currentConnectorStatus === ConnectorStatusEnum.PREPARING;

                return (
                    <Button
                        variant="contained"
                        color="primary"
                        sx={guestChargingSx.primaryButton}
                        onClick={handleProceedToPayment}
                        disabled={!isPreparing || isCreatingSession}
                    >
                        {isCreatingSession ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : isPreparing ? (
                            intl.formatMessage({id: 'guestCharging.proceedToPayment'})
                        ) : (
                            intl.formatMessage({id: 'guestCharging.plugInCable'})
                        )}
                    </Button>
                );

            case 'starting_charge':
                return (
                    <Button
                        variant="contained"
                        sx={guestChargingSx.errorButton}
                        disabled
                    >
                        {intl.formatMessage({id: 'guestCharging.stopCharging'})}
                    </Button>
                );

            case 'charging':
                return (
                    <Button
                        variant="contained"
                        sx={guestChargingSx.errorButton}
                        onClick={handleStopCharging}
                        disabled={isStoppingCharging}
                    >
                        {isStoppingCharging ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            intl.formatMessage({id: 'guestCharging.stopCharging'})
                        )}
                    </Button>
                );

            case 'stopping_charge':
                return (
                    <Button
                        variant="contained"
                        sx={guestChargingSx.successButton}
                        disabled
                    >
                        {intl.formatMessage({id: 'guestCharging.newSession'})}
                    </Button>
                );

            case 'error':
            case 'cancelled':
            case 'not_found':
                return (
                    <Button
                        variant="contained"
                        sx={guestChargingSx.secondaryButton}
                        onClick={handleTryAgain}
                    >
                        {intl.formatMessage({id: 'guestCharging.tryAgain'})}
                    </Button>
                );

            case 'completed':
                return (
                    <Button
                        variant="contained"
                        sx={guestChargingSx.successButton}
                        onClick={handleTryAgain}
                    >
                        {intl.formatMessage({id: 'guestCharging.newSession'})}
                    </Button>
                );

            default:
                return null;
        }
    };

    const footerContent = renderFooter();

    return (
        <Box sx={guestChargingSx.root}>
            <Box sx={guestChargingSx.pageWrapper}>
                {/* Header */}
                <Box sx={guestChargingSx.header}>
                    <LogoBlack />
                    <LangSelect />
                </Box>

                {/* Content */}
                <Box sx={guestChargingSx.content}>{renderContent()}</Box>

                {/* Footer with an action button */}
                {footerContent && <Box sx={guestChargingSx.footer}>{footerContent}</Box>}
            </Box>
        </Box>
    );
};